import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import Aos from "aos";
import "aos/dist/aos.css";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// ── Animations ────────────────────────────────────────────────────────────────
const scrollLeft = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// ✅ RTL: inverse direction
const scrollRight = keyframes`
  0%   { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50%       { opacity: 1;   transform: scale(1.05); }
`;

// ── Styled Components ─────────────────────────────────────────────────────────
const Section = styled.section`
  background: #010606;
  padding: 50px 0 70px;
  overflow: hidden;
  position: relative;

  /* ligne verte épaisse + glow pour séparer visuellement */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, transparent 0%, #18e68e 40%, #18e68e 60%, transparent 100%);
    box-shadow: 0 0 18px #18e68e99;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, #18e68e33, transparent);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  color: white;
  margin-bottom: 10px;

  span {
    color: #18e68e;
  }

  @media screen and (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #b3b3b3;
  max-width: 500px;
  margin: 0 auto;

  @media screen and (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const TrackWrapper = styled.div`
  position: relative;
  z-index: 1;
  /* ✅ Force LTR on the track regardless of page direction */
  direction: ltr;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 120px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, #010606 0%, transparent 100%);
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, #010606 0%, transparent 100%);
  }
`;

// ✅ isRTL prop controls which animation plays
// vitesse : 30s → 60s (plus lent)
const Track = styled.div`
  display: flex;
  width: max-content;
  ${({ $isRTL }) =>
    $isRTL
      ? css`animation: ${scrollRight} 40s linear infinite;`
      : css`animation: ${scrollLeft}  40s linear infinite;`}

  &:hover {
    animation-play-state: paused;
  }
`;


const PartnerCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgb(39, 38, 38);
  border: 1px solid #18e68e22;
  border-radius: 14px;
  padding: 16px 12px;
  margin: 0 6px;
  width: 160px;
  min-height: 160px;
  cursor: pointer;
  transition: border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    border-color: #18e68e88;
    box-shadow: 0 0 28px #18e68e33;
    transform: translateY(-4px);
  }
`;

const AvatarWrapper = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #18e68e55;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1a1a1a 0%, #222 50%, #1a1a1a 100%);
  background-size: 200% 200%;
  animation: ${shimmer} 2s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: #18e68e;
  font-weight: bold;
`;

const PartnerName = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
  text-align: center;
  line-height: 1.3;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 28px;
  position: relative;
  z-index: 1;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #18e68e;
  animation: ${pulse} 2s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}s;
`;

// ── Main Component ────────────────────────────────────────────────────────────
const PartnersSection = ({ partners = [], apiBase = "", isRTL = false }) => {
  const { t } = useTranslation();

  useEffect(() => {
    Aos.init({ duration: 1000, easing: "ease-in-out", once: true });
  }, []);

  // duplicate list so the infinite scroll looks seamless
  const displayed = [...partners, ...partners];

  const getAvatarUrl = (company) => {
    if (!company.photoprincipal) return null;
    return `${apiBase}/company/photoprincipal/${company.photoprincipal}`;
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  return (
    <Section id="partners" className={isRTL ? "arabe" : ""}>
      <Header data-aos="fade-up">
        <Title>
          <span>{t("partners.title1")}</span> {t("partners.title")}
        </Title>
        <Subtitle>{t("partners.subtitle")}</Subtitle>
      </Header>

      <TrackWrapper data-aos="fade-up" data-aos-delay="100">
        {/* ✅ direction always LTR on the track, animation switches based on isRTL */}
        <Track $isRTL={isRTL}>
          {displayed.map((company, idx) => (
            <PartnerCard key={`${company._id}-${idx}`}>
              <AvatarWrapper>
                <PartnerAvatar
                  src={getAvatarUrl(company)}
                  initials={getInitials(company.company_name)}
                />
              </AvatarWrapper>
              <PartnerName>{company.company_name || "Partner"}</PartnerName>
            </PartnerCard>
          ))}
        </Track>
      </TrackWrapper>

      <Dots data-aos="fade-up" data-aos-delay="200">
        <Dot delay={0} />
        <Dot delay={0.3} />
        <Dot delay={0.6} />
      </Dots>
    </Section>
  );
};

// ── Sub-component ─────────────────────────────────────────────────────────────
const PartnerAvatar = ({ src, initials }) => {
  const [error, setError] = useState(false);

  if (!src || error) {
    return <AvatarPlaceholder>{initials || "?"}</AvatarPlaceholder>;
  }

  return (
    <Avatar
      src={src}
      alt={initials}
      onError={() => setError(true)}
    />
  );
};

PartnersSection.propTypes = {
  partners: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      company_name: PropTypes.string,
      photoprincipal: PropTypes.string,
    })
  ),
  apiBase: PropTypes.string,
  isRTL: PropTypes.bool,
};

PartnerAvatar.propTypes = {
  src: PropTypes.string,
  initials: PropTypes.string,
};

export default PartnersSection;
